import { Archive, History, MessageSquarePlus, Trash2, ArrowLeft, Loader2 } from 'lucide-react';
import type { ChatConversationSummary, ChatHistoryMessage } from './chat.api';

interface Props {
  conversations: ChatConversationSummary[];
  selectedId: string | null;
  historyMessages: ChatHistoryMessage[];
  loadingList: boolean;
  loadingMessages: boolean;
  onSelect: (id: string | null) => void;
  onDelete: (id: string) => void;
  onArchive: (id: string) => void;
  onNewChat: () => void;
  onClose: () => void;
  onLoadConversation: (messages: ChatHistoryMessage[]) => void;
}

function formatDate(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Дөнгөж сая';
  if (diffMin < 60) return `${diffMin} мин өмнө`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} цагийн өмнө`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD} өдрийн өмнө`;
  return d.toLocaleDateString('mn-MN', { month: 'short', day: 'numeric' });
}

export default function ChatHistoryDrawer({
  conversations,
  selectedId,
  historyMessages,
  loadingList,
  loadingMessages,
  onSelect,
  onDelete,
  onArchive,
  onNewChat,
  onClose,
  onLoadConversation,
}: Props) {
  if (selectedId && historyMessages.length > 0) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 dark:border-slate-700">
          <button
            onClick={() => onSelect(null)}
            className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            <ArrowLeft size={16} />
          </button>
          <span className="text-sm font-medium truncate flex-1">
            {conversations.find((c) => c.id === selectedId)?.title || 'Харилцлага'}
          </span>
          <button
            onClick={() => {
              onLoadConversation(historyMessages);
              onClose();
            }}
            className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Нээх
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {historyMessages.map((msg) => (
            <div
              key={msg.id}
              className={`text-sm rounded-lg px-3 py-2 ${
                msg.role === 'USER'
                  ? 'bg-blue-50 dark:bg-blue-900/30 ml-6'
                  : 'bg-slate-50 dark:bg-slate-800 mr-6'
              }`}
            >
              <div className="text-[10px] text-slate-400 mb-1">
                {msg.role === 'USER' ? 'Та' : 'AI'}
              </div>
              <div className="whitespace-pre-wrap break-words leading-relaxed">
                {msg.textRedacted}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-2">
          <History size={16} />
          <span className="text-sm font-medium">Чат түүх</span>
        </div>
        <button
          onClick={onNewChat}
          className="flex items-center gap-1 text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          <MessageSquarePlus size={12} />
          Шинэ чат
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {loadingList && (
          <div className="flex justify-center py-8">
            <Loader2 size={20} className="animate-spin text-slate-400" />
          </div>
        )}
        {!loadingList && conversations.length === 0 && (
          <div className="text-center py-8 text-sm text-slate-400">
            Чат түүх хоосон байна
          </div>
        )}
        {conversations.map((conv) => (
          <div
            key={conv.id}
            className={`group flex items-center gap-2 px-3 py-2.5 cursor-pointer border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 ${
              selectedId === conv.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
            }`}
          >
            <div className="flex-1 min-w-0" onClick={() => onSelect(conv.id)}>
              <div className="flex items-center gap-1.5">
                <div className="min-w-0 truncate text-sm font-medium">
                  {conv.title || 'Шинэ харилцлага'}
                </div>
                {conv.status === 'ARCHIVED' && (
                  <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                    Архив
                  </span>
                )}
              </div>
              <div className="text-[11px] text-slate-400 truncate">
                {formatDate(conv.lastMessageAt)}
              </div>
            </div>
            {conv.status !== 'ARCHIVED' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onArchive(conv.id);
                }}
                className="p-1 rounded opacity-0 transition-opacity group-hover:opacity-100 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-slate-100 shrink-0"
                aria-label="Чат архивлах"
              >
                <Archive size={14} />
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(conv.id);
              }}
              className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-500 shrink-0"
              style={{ opacity: selectedId === conv.id ? 1 : undefined }}
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
      {loadingMessages && (
        <div className="flex justify-center py-2 border-t border-slate-200 dark:border-slate-700">
          <Loader2 size={16} className="animate-spin text-slate-400" />
        </div>
      )}
    </div>
  );
}
